/**
 * The collision half of `validateKeymap` (plan D6). Candidates arrive sorted
 * by precedence; each is accepted, or loses to an already accepted chord of an
 * overlapping scope. A loss is durable (the loser's override is patched) only
 * when it is not layout-dependent and either involves a chord the current
 * mutation added — and, for a scheme or Jam default, took a chord that was
 * active before that change (O1 ii) — or is between two stored overrides,
 * holds on every platform and repairs are not deferred (layout still loading). A scheme or Jam default
 * that loses to a stored override is only inactive (`default-shadowed`) and
 * nothing is written, so the command keeps following the active scheme — a
 * scheme switch never writes an override for the losing command. Everything
 * else is a non-durable notice too.
 */
import { layoutTokenForCode, parseChord } from './chord';
import { resolvedChordValue } from './chordIdentity';
import { defaultKeybindings } from './defaultKeybindings';
import { createKeybindingNotice, isAddedByMutation } from './keymapWarnings';
import { aliasModifiersOf, usLayoutAlias } from './usLayoutAlias';
import type {
  Chord,
  KeybindingCommandId,
  KeybindingNotice,
  KeyboardLayoutSnapshot,
  Platform,
} from './keybindingTypes';

export type ChordSignature = { readonly value: string; readonly logicalAlias?: string };

export type Candidate = {
  readonly commandId: KeybindingCommandId;
  readonly chord: string;
  readonly signature: ChordSignature;
  readonly priority: number;
  /** The chord comes from the command's stored override, not from the scheme or Jam defaults. */
  readonly stored: boolean;
  readonly registryIndex: number;
  readonly chordIndex: number;
};

export type CollisionContext = {
  readonly mutatedCommandId?: KeybindingCommandId;
  readonly mutatedChords?: readonly string[];
  /** Layout still loading: no durable repair except for chords the mutation added (I1). */
  readonly deferRepairs: boolean;
  /**
   * During a mutation: whether a chord was active in the keymap before it
   * (O1 ii). A default chord that was already inactive loses to an added chord
   * without anything being written for its row. Absent: every chord counts as active.
   */
  readonly activeBefore?: (commandId: KeybindingCommandId, chord: string) => boolean;
  readonly notices: KeybindingNotice[];
  /** Called for a durable loss: remove `chord` from `commandId`'s stored chords. */
  readonly removeDurably: (commandId: KeybindingCommandId, chord: string) => void;
};

/**
 * The logical key a physical chord's code types. The layout says when it has
 * a printable ASCII entry. When the layout is unavailable, or ready without
 * one for the code (no entry, or — on a non-Latin letter layout — a non-ASCII
 * entry such as KeyS → 'ы', FR1), a physical chord still matches on `code`
 * while the logical chord matches the code's US-layout key — one press would
 * run two commands — so the US key stands in (`usLayoutAlias`, shared with
 * the dispatcher and the reserved-chord pass) and the overlap is
 * layout-dependent (never durable). A Latin layout's own non-ASCII key (ö, é)
 * gets no alias. No alias where the press cannot match the logical chord: with
 * Shift held, a digit or punctuation key types its shifted character, which
 * only the layout knows; on macOS, Option changes `key` for every key
 * (Option+F types ƒ). While the layout is loading a physical chord matches
 * nothing, so it has no alias either.
 */
function physicalAlias(chord: Chord, platform: Platform, layout: KeyboardLayoutSnapshot): string | null {
  const mapped = layoutTokenForCode(chord.key, layout);
  return mapped ?? usLayoutAlias(chord.key, aliasModifiersOf(chord.modifiers), platform, layout);
}

export function signature(chord: Chord, platform: Platform, layout: KeyboardLayoutSnapshot): ChordSignature | null {
  if (platform !== 'mac' && chord.modifiers.includes('Cmd')) return null;
  const modifiers = new Set(chord.modifiers.map((modifier) => modifier === 'Mod'
    ? (platform === 'mac' ? 'Cmd' : 'Ctrl')
    : modifier));
  const prefix = `${[...modifiers].sort().join('+')}|`;
  const mapped = chord.physical ? physicalAlias(chord, platform, layout) : null;
  return {
    value: `${prefix}${chord.physical ? 'physical' : 'logical'}:${chord.key}`,
    ...(mapped ? { logicalAlias: `${prefix}logical:${mapped}` } : {}),
  };
}

function signaturesCollide(left: ChordSignature, right: ChordSignature): { readonly layoutDependent: boolean } | null {
  if (left.value === right.value) return { layoutDependent: false };
  if (left.logicalAlias === right.value || right.logicalAlias === left.value) return { layoutDependent: true };
  return null;
}

function scopesOverlap(left: KeybindingCommandId, right: KeybindingCommandId): boolean {
  const a = defaultKeybindings[left].scope;
  const b = defaultKeybindings[right].scope;
  if (a === 'global' || b === 'global' || a === b) return true;
  return (a === 'query-editor' && b === 'sql-editor') || (a === 'sql-editor' && b === 'query-editor') ||
    (a === 'notebook' && b === 'sql-editor') || (a === 'sql-editor' && b === 'notebook') ||
    (a === 'plsql-editor' && b === 'debugger') || (a === 'debugger' && b === 'plsql-editor');
}

const PLATFORMS: readonly Platform[] = ['mac', 'windows', 'linux'];

/** False when two colliding chords are distinct somewhere (`Ctrl+Enter` vs `Mod+Enter` on macOS). */
function collidesOnEveryPlatform(left: string, right: string): boolean {
  const a = parseChord(left);
  const b = parseChord(right);
  return Boolean(a && b) && PLATFORMS.every((platform) => {
    const value = resolvedChordValue(a as Chord, platform);
    return value !== null && value === resolvedChordValue(b as Chord, platform);
  });
}

const isGlobal = (commandId: KeybindingCommandId): boolean => defaultKeybindings[commandId].scope === 'global';
const label = (commandId: KeybindingCommandId): string => defaultKeybindings[commandId].label;

export function resolveCollisions(candidates: readonly Candidate[], context: CollisionContext): Candidate[] {
  const accepted: Candidate[] = [];
  // Accepted chords plus same-row duplicates: a duplicate is left out of the
  // keymap, but it still claims its keys, so a third command's identical
  // chord collides with it exactly as it would on a layout without the alias.
  const claimants: Candidate[] = [];
  const added = ({ commandId, chord }: Candidate) =>
    isAddedByMutation(commandId, chord, context.mutatedCommandId, context.mutatedChords);
  const markDuplicate = (candidate: Candidate, of: Candidate) => context.notices.push(createKeybindingNotice(
    'duplicate', candidate.commandId, candidate.chord,
    `${candidate.chord} is the same keys as ${of.chord} on this keyboard layout.`, false, undefined, of.chord,
  ));

  for (const candidate of candidates) {
    const overlaps = claimants.flatMap((item) => {
      const collision = signaturesCollide(item.signature, candidate.signature);
      return collision && scopesOverlap(item.commandId, candidate.commandId) ? [{ item, collision }] : [];
    });
    // A chord with the same keys as an ACCEPTED chord of its own row is a
    // duplicate. An inactive duplicate claims its keys against other commands
    // only — it never suppresses a distinct chord of its own row — and an
    // overlap with another command is resolved as usual.
    const duplicateOf = overlaps.find(({ item }) =>
      item.commandId === candidate.commandId && accepted.includes(item))?.item;
    const others = overlaps.filter(({ item }) => item.commandId !== candidate.commandId);
    const acceptOrDuplicate = () => {
      if (duplicateOf) markDuplicate(candidate, duplicateOf);
      else accepted.push(candidate);
      claimants.push(candidate);
    };
    if (others.length === 0) { acceptOrDuplicate(); continue; }

    const sameScope = others.filter(({ item }) => isGlobal(item.commandId) === isGlobal(candidate.commandId));
    // A collision that holds on every layout outranks a layout alias of the same chord.
    const sameScopeWinner = sameScope.find(({ collision }) => !collision.layoutDependent) ?? sameScope[0];
    if (!sameScopeWinner) {
      const winner = others[0].item;
      context.notices.push(createKeybindingNotice('scoped-global-overlap', candidate.commandId, candidate.chord, `${candidate.chord} is shared with ${label(winner.commandId)}; the scoped command wins in its context.`, false, winner.commandId));
      acceptOrDuplicate();
      continue;
    }
    const { item: winner, collision } = sameScopeWinner;
    const layoutDependent = collision.layoutDependent;
    const universal = collidesOnEveryPlatform(winner.chord, candidate.chord);
    const wasActive = candidate.stored || (context.activeBefore?.(candidate.commandId, candidate.chord) ?? true);
    const durableLoss = !layoutDependent && (([winner, candidate].some(added) && wasActive) ||
      (universal && !context.deferRepairs && candidate.stored && winner.stored));
    const shadowedDefault = !layoutDependent && !durableLoss && universal && !candidate.stored;
    const message = shadowedDefault
      ? `${candidate.chord} is used by ${label(winner.commandId)}; this default is inactive while that holds.`
      : !layoutDependent && !durableLoss
        ? `${candidate.chord} is the same shortcut as ${label(winner.commandId)}'s on this platform; it stays saved but is inactive.`
        : `${candidate.chord} moved to ${label(winner.commandId)}; ${label(candidate.commandId)} lost this binding.`;
    const kind = layoutDependent ? 'layout-collision' : shadowedDefault ? 'default-shadowed' : 'collision';
    context.notices.push(createKeybindingNotice(kind, candidate.commandId, candidate.chord, message, durableLoss, winner.commandId));
    if (durableLoss) context.removeDurably(candidate.commandId, candidate.chord);
  }
  return accepted;
}
